import { useState, useCallback, useRef } from 'react'
import type { PlayingCard, ChipValue } from '@/types'
import {
  createDeck,
  calculateHandValue,
  isBlackjack,
  isBusted,
  canSplit as checkCanSplit,
  determineWinner,
  calculatePayout,
  type GameResult,
} from '../utils/blackjackLogic'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import { delay } from '@/lib/utils'
import { recordGameTransaction } from '@/features/stats'

export type BlackjackPhase = 'betting' | 'dealing' | 'playerTurn' | 'dealerTurn' | 'result'
export type DealingTarget = 'player' | 'dealer' | null

interface UseBlackjackGameReturn {
  // State
  playerHand: PlayingCard[]
  dealerHand: PlayingCard[]
  betAmount: number
  selectedChip: ChipValue
  gamePhase: BlackjackPhase
  result: GameResult | null
  lastWinnings: number
  isDealing: boolean
  showDealerCard: boolean
  dealingTo: DealingTarget

  // Computed
  playerValue: number
  dealerValue: number
  canDouble: boolean
  canSplit: boolean

  // Actions
  addBet: (value: number) => void
  selectChip: (value: ChipValue) => void
  clearBet: () => void
  deal: () => Promise<void>
  hit: () => Promise<void>
  stand: () => Promise<void>
  double: () => Promise<void>
  newGame: () => void
}

export function useBlackjackGame(): UseBlackjackGameReturn {
  const { user, updateBalance, isDemo } = useAuth()
  const deckRef = useRef<PlayingCard[]>([])

  const [playerHand, setPlayerHand] = useState<PlayingCard[]>([])
  const [dealerHand, setDealerHand] = useState<PlayingCard[]>([])
  const [betAmount, setBetAmount] = useState(0)
  const [selectedChip, setSelectedChip] = useState<ChipValue>(100)
  const [gamePhase, setGamePhase] = useState<BlackjackPhase>('betting')
  const [result, setResult] = useState<GameResult | null>(null)
  const [lastWinnings, setLastWinnings] = useState(0)
  const [isDealing, setIsDealing] = useState(false)
  const [showDealerCard, setShowDealerCard] = useState(false)
  const [dealingTo, setDealingTo] = useState<DealingTarget>(null)

  // Ensure deck has cards
  const ensureDeck = useCallback(() => {
    if (deckRef.current.length < 52) {
      deckRef.current = createDeck()
    }
  }, [])

  // Draw a card
  const drawCard = useCallback((): PlayingCard => {
    ensureDeck()
    return deckRef.current.pop()!
  }, [ensureDeck])

  // Computed values
  const playerValue = calculateHandValue(playerHand)
  const dealerValue = showDealerCard
    ? calculateHandValue(dealerHand)
    : dealerHand.length > 0
      ? calculateHandValue([dealerHand[0]])
      : 0

  const canDouble = gamePhase === 'playerTurn' && playerHand.length === 2 && (user?.balance || 0) >= betAmount
  const canSplit = gamePhase === 'playerTurn' && checkCanSplit(playerHand) && (user?.balance || 0) >= betAmount

  // Add bet
  const addBet = useCallback((value: number) => {
    if (gamePhase !== 'betting' || isDealing) return
    if (!user) return

    const newBet = betAmount + value
    if (newBet <= user.balance) {
      setBetAmount(newBet)
    }
  }, [gamePhase, isDealing, user, betAmount])

  // Select chip
  const selectChip = useCallback((value: ChipValue) => {
    if (gamePhase !== 'betting') return
    setSelectedChip(value)
  }, [gamePhase])

  // Clear bet
  const clearBet = useCallback(() => {
    if (gamePhase !== 'betting') return
    setBetAmount(0)
  }, [gamePhase])

  // Deal cards
  const deal = useCallback(async () => {
    if (betAmount <= 0 || isDealing || !user) return
    if (betAmount > user.balance) return

    setIsDealing(true)
    setGamePhase('dealing')
    setShowDealerCard(false)

    // Deduct bet
    await updateBalance(-betAmount)

    // Deal initial cards con animaciones más lentas
    const newPlayerHand: PlayingCard[] = []
    const newDealerHand: PlayingCard[] = []

    // Card 1 to player
    setDealingTo('player')
    await delay(850)
    newPlayerHand.push(drawCard())
    setPlayerHand([...newPlayerHand])
    setDealingTo(null)

    // Card 1 to dealer
    await delay(400)
    setDealingTo('dealer')
    await delay(850)
    newDealerHand.push(drawCard())
    setDealerHand([...newDealerHand])
    setDealingTo(null)

    // Card 2 to player
    await delay(400)
    setDealingTo('player')
    await delay(850)
    newPlayerHand.push(drawCard())
    setPlayerHand([...newPlayerHand])
    setDealingTo(null)

    // Card 2 to dealer
    await delay(400)
    setDealingTo('dealer')
    await delay(850)
    newDealerHand.push(drawCard())
    setDealerHand([...newDealerHand])
    setDealingTo(null)

    setIsDealing(false)

    // Check for blackjack
    const playerBJ = isBlackjack(newPlayerHand)
    const dealerBJ = isBlackjack(newDealerHand)

    if (playerBJ || dealerBJ) {
      setShowDealerCard(true)
      await delay(500)

      const gameResult = determineWinner(
        calculateHandValue(newPlayerHand),
        calculateHandValue(newDealerHand),
        playerBJ,
        dealerBJ
      )

      const winnings = calculatePayout(gameResult, betAmount)
      setResult(gameResult)
      setLastWinnings(winnings)

      if (winnings > 0) {
        await updateBalance(winnings)
      }

      // Record game transaction for stats
      if (!isDemo && user) {
        const statResult = gameResult === 'win' || gameResult === 'blackjack' ? 'win'
          : gameResult === 'push' ? 'push' : 'loss'
        recordGameTransaction(
          user.uid,
          user.email,
          user.displayName || 'Usuario',
          'blackjack',
          betAmount,
          winnings,
          statResult,
          { result: gameResult, playerValue: calculateHandValue(newPlayerHand), dealerValue: calculateHandValue(newDealerHand) }
        )
      }

      setGamePhase('result')
      return
    }

    setGamePhase('playerTurn')
  }, [betAmount, isDealing, user, updateBalance, drawCard, isDemo])

  // Hit - draw another card
  const hit = useCallback(async () => {
    if (gamePhase !== 'playerTurn' || isDealing) return

    setIsDealing(true)
    setDealingTo('player')

    await delay(850)
    const newHand = [...playerHand, drawCard()]
    setPlayerHand(newHand)
    setDealingTo(null)

    await delay(300)
    setIsDealing(false)

    if (isBusted(newHand)) {
      setShowDealerCard(true)
      setResult('bust')
      setLastWinnings(0)

      // Record game transaction for stats
      if (!isDemo && user) {
        recordGameTransaction(
          user.uid,
          user.email,
          user.displayName || 'Usuario',
          'blackjack',
          betAmount,
          0,
          'loss',
          { result: 'bust', playerValue: calculateHandValue(newHand) }
        )
      }

      setGamePhase('result')
    }
  }, [gamePhase, isDealing, playerHand, drawCard, isDemo, user, betAmount])

  // Stand - end player turn
  const stand = useCallback(async () => {
    if (gamePhase !== 'playerTurn' || isDealing) return

    setGamePhase('dealerTurn')
    setShowDealerCard(true)
    setIsDealing(true)

    await delay(500)

    // Dealer draws until 17
    let currentDealerHand = [...dealerHand]
    while (calculateHandValue(currentDealerHand) < 17) {
      setDealingTo('dealer')
      await delay(850)
      currentDealerHand = [...currentDealerHand, drawCard()]
      setDealerHand([...currentDealerHand])
      setDealingTo(null)
      await delay(400)
    }

    await delay(500)
    setIsDealing(false)

    // Determine winner
    const finalPlayerValue = calculateHandValue(playerHand)
    const finalDealerValue = calculateHandValue(currentDealerHand)

    const gameResult = determineWinner(finalPlayerValue, finalDealerValue, false, false)
    const winnings = calculatePayout(gameResult, betAmount)

    setResult(gameResult)
    setLastWinnings(winnings)

    if (winnings > 0) {
      await updateBalance(winnings)
    }

    // Record game transaction for stats
    if (!isDemo && user) {
      const statResult = gameResult === 'win' ? 'win' : gameResult === 'push' ? 'push' : 'loss'
      recordGameTransaction(
        user.uid,
        user.email,
        user.displayName || 'Usuario',
        'blackjack',
        betAmount,
        winnings,
        statResult,
        { result: gameResult, playerValue: finalPlayerValue, dealerValue: finalDealerValue }
      )
    }

    setGamePhase('result')
  }, [gamePhase, isDealing, dealerHand, playerHand, betAmount, drawCard, updateBalance, isDemo, user])

  // Double down
  const double = useCallback(async () => {
    if (!canDouble || isDealing || !user) return

    setIsDealing(true)

    // Double the bet
    await updateBalance(-betAmount)
    const doubleBet = betAmount * 2
    setBetAmount(doubleBet)

    // Draw one card with animation
    setDealingTo('player')
    await delay(850)
    const newHand = [...playerHand, drawCard()]
    setPlayerHand(newHand)
    setDealingTo(null)

    await delay(300)
    setIsDealing(false)

    if (isBusted(newHand)) {
      setShowDealerCard(true)
      setResult('bust')
      setLastWinnings(0)

      // Record game transaction for stats (doubled bet)
      if (!isDemo) {
        recordGameTransaction(
          user.uid,
          user.email,
          user.displayName || 'Usuario',
          'blackjack',
          doubleBet,
          0,
          'loss',
          { result: 'bust', playerValue: calculateHandValue(newHand), doubled: true }
        )
      }

      setGamePhase('result')
      return
    }

    // Auto-stand after double
    setGamePhase('dealerTurn')
    setShowDealerCard(true)
    setIsDealing(true)

    await delay(500)

    // Dealer draws
    let currentDealerHand = [...dealerHand]
    while (calculateHandValue(currentDealerHand) < 17) {
      setDealingTo('dealer')
      await delay(850)
      currentDealerHand = [...currentDealerHand, drawCard()]
      setDealerHand([...currentDealerHand])
      setDealingTo(null)
      await delay(400)
    }

    await delay(500)
    setIsDealing(false)

    const finalPlayerValue = calculateHandValue(newHand)
    const finalDealerValue = calculateHandValue(currentDealerHand)

    const gameResult = determineWinner(finalPlayerValue, finalDealerValue, false, false)
    const winnings = calculatePayout(gameResult, doubleBet)

    setResult(gameResult)
    setLastWinnings(winnings)

    if (winnings > 0) {
      await updateBalance(winnings)
    }

    // Record game transaction for stats (doubled bet)
    if (!isDemo) {
      const statResult = gameResult === 'win' ? 'win' : gameResult === 'push' ? 'push' : 'loss'
      recordGameTransaction(
        user.uid,
        user.email,
        user.displayName || 'Usuario',
        'blackjack',
        doubleBet,
        winnings,
        statResult,
        { result: gameResult, playerValue: finalPlayerValue, dealerValue: finalDealerValue, doubled: true }
      )
    }

    setGamePhase('result')
  }, [canDouble, isDealing, user, betAmount, playerHand, dealerHand, updateBalance, drawCard, isDemo])

  // New game
  const newGame = useCallback(() => {
    setPlayerHand([])
    setDealerHand([])
    setBetAmount(0)
    setGamePhase('betting')
    setResult(null)
    setLastWinnings(0)
    setIsDealing(false)
    setShowDealerCard(false)
  }, [])

  return {
    playerHand,
    dealerHand,
    betAmount,
    selectedChip,
    gamePhase,
    result,
    lastWinnings,
    isDealing,
    showDealerCard,
    dealingTo,
    playerValue,
    dealerValue,
    canDouble,
    canSplit,
    addBet,
    selectChip,
    clearBet,
    deal,
    hit,
    stand,
    double,
    newGame,
  }
}
